import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { TbPlus, TbEdit, TbTrash, TbFilter } from 'react-icons/tb';

import PageHeader from '../../components/misc/PageHeader';
import Button from '../../components/buttons/Button';
import ModalLayout from '../../components/layouts/ModalLayout';
import Input from '../../components/misc/Input';
import Labeled from '../../components/misc/Labeled';
import DropdownSelect from '../../components/forms/DropdownSelect';
import useSelectedCommunity from '../../../hooks/community/useSelectedCommunity';
import * as bankAccountService from '../../../services/community/bankAccountService';
import {
  CategorizationRule,
  CreateCategorizationRuleDto,
  FinancialCategory,
} from '../../../types/bankAccount';

interface CategorizationRulesSettingsPageProps {
  hideHeader?: boolean;
}

const MATCH_TYPES: Array<{ value: 'CONTAINS' | 'EQUALS' | 'REGEX'; label: string }> = [
  { value: 'CONTAINS', label: 'Contains' },
  { value: 'EQUALS', label: 'Equals' },
  { value: 'REGEX', label: 'Regex' },
];

export default function CategorizationRulesSettingsPage({
  hideHeader = false,
}: CategorizationRulesSettingsPageProps): JSX.Element {
  const { t } = useTranslation('', {
    keyPrefix: 'page.financial.categorizationRules',
  });
  const community = useSelectedCommunity();
  const [rules, setRules] = useState<CategorizationRule[]>([]);
  const [categories, setCategories] = useState<FinancialCategory[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [createOpen, setCreateOpen] = useState<boolean>(false);
  const [editing, setEditing] = useState<CategorizationRule | null>(null);
  const [applying, setApplying] = useState<boolean>(false);

  const [form, setForm] = useState<CreateCategorizationRuleDto>({
    name: '',
    matchType: 'CONTAINS',
    matchText: '',
    financialCategoryId: 0,
    priority: 0,
    isActive: true,
  });

  const refresh = useCallback(async () => {
    if (!community) return;
    setLoading(true);
    try {
      const [rulesData, categoriesData] = await Promise.all([
        bankAccountService.listCategorizationRules(),
        bankAccountService.getCategories(),
      ]);
      setRules(rulesData);
      setCategories(categoriesData);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [community]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const resetForm = () => {
    setForm({
      name: '',
      matchType: 'CONTAINS',
      matchText: '',
      financialCategoryId: categories[0]?.financialCategoryId ?? 0,
      priority: 0,
      isActive: true,
    });
  };

  const openCreateModal = () => {
    resetForm();
    setEditing(null);
    setCreateOpen(true);
  };

  const openEditModal = (rule: CategorizationRule) => {
    setEditing(rule);
    setForm({
      name: rule.name,
      matchType: rule.matchType,
      matchText: rule.matchText,
      financialCategoryId: rule.financialCategoryId,
      priority: rule.priority,
      isActive: rule.isActive,
    });
    setCreateOpen(true);
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      toast.error(t('errors.nameRequired') ?? 'Name required');
      return;
    }
    if (!form.matchText.trim()) {
      toast.error(t('errors.matchTextRequired') ?? 'Pattern required');
      return;
    }
    if (!form.financialCategoryId) {
      toast.error(t('errors.categoryRequired') ?? 'Pick a category');
      return;
    }
    try {
      if (editing) {
        await bankAccountService.updateCategorizationRule(editing.categorizationRuleId, form);
        toast.success(t('success.updated') ?? 'Rule updated');
      } else {
        await bankAccountService.createCategorizationRule(form);
        toast.success(t('success.created') ?? 'Rule created');
      }
      setCreateOpen(false);
      setEditing(null);
      resetForm();
      refresh();
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const handleDelete = async (rule: CategorizationRule) => {
    if (!window.confirm(t('confirmations.delete', { name: rule.name }) ?? `Delete "${rule.name}"?`)) return;
    try {
      await bankAccountService.deleteCategorizationRule(rule.categorizationRuleId);
      toast.success(t('success.deleted') ?? 'Rule deleted');
      refresh();
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const handleApplyAll = async () => {
    setApplying(true);
    try {
      const result = await bankAccountService.applyCategorizationRulesToAll();
      toast.success(
        t('success.applied', {
          count: result.assignmentsCreated,
          lines: result.totalLinesScanned,
          defaultValue: `Applied rules — ${result.assignmentsCreated} assignments across ${result.totalLinesScanned} line(s).`,
        }) ?? `Applied rules (${result.assignmentsCreated} assignments).`,
      );
      refresh();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setApplying(false);
    }
  };

  const categoryOptions = categories.map((c) => ({ value: c.financialCategoryId, label: c.name }));
  const matchTypeOptions = MATCH_TYPES.map((m) => ({ value: m.value, label: m.label }));

  return (
    <>
      {!hideHeader && (
        <PageHeader
          title={t('title')}
          subtitle={t('subtitle')}
          primaryButton={
            <Button variant="primary-dark" onClick={openCreateModal}>
              <TbPlus />
              {t('addRule')}
            </Button>
          }
        />
      )}

      {hideHeader && (
        <div className="mb-4 flex flex-wrap justify-end gap-2">
          <Button variant="secondary" onClick={handleApplyAll} disabled={applying}>
            <TbFilter />
            {applying ? (t('applying') ?? 'Applying…') : (t('applyToExisting') ?? 'Apply to existing')}
          </Button>
          <Button variant="primary-dark" onClick={openCreateModal}>
            <TbPlus />
            {t('addRule')}
          </Button>
        </div>
      )}

      <div className="space-y-4">
        {loading ? (
          <div className="py-10 text-center text-sm text-primary-900/60">Loading…</div>
        ) : rules.length === 0 ? (
          <div className="py-12 text-center text-sm text-primary-900/60">
            {t('emptyState') ?? 'No categorization rules yet. Create one to auto-assign categories on new transactions.'}
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg border border-secondary-200 bg-white">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-secondary-50">
                <tr>
                  <th className="px-4 py-2 text-left font-medium text-primary-900">{t('columns.name')}</th>
                  <th className="px-4 py-2 text-left font-medium text-primary-900">{t('columns.match')}</th>
                  <th className="px-4 py-2 text-left font-medium text-primary-900">{t('columns.category')}</th>
                  <th className="px-4 py-2 text-right font-medium text-primary-900">{t('columns.priority')}</th>
                  <th className="px-4 py-2 text-center font-medium text-primary-900">{t('columns.active')}</th>
                  <th className="px-4 py-2 text-right font-medium text-primary-900">{t('columns.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-secondary-100">
                {rules.map((rule) => {
                  const cat = categories.find((c) => c.financialCategoryId === rule.financialCategoryId);
                  return (
                    <tr key={rule.categorizationRuleId}>
                      <td className="px-4 py-2 font-medium text-primary-900">{rule.name}</td>
                      <td className="px-4 py-2 text-primary-900/80">
                        <span className="rounded bg-secondary-100 px-2 py-0.5 text-xs font-mono">
                          {`${rule.matchType}`}
                        </span>{' '}
                        <span className="font-mono text-xs">{rule.matchText}</span>
                      </td>
                      <td className="px-4 py-2">
                        {cat && (
                          <span
                            className="inline-flex items-center gap-2 rounded-full px-2 py-0.5 text-xs"
                            style={{ backgroundColor: `${cat.color}33`, color: cat.color }}>
                            <span
                              className="h-2 w-2 rounded-full"
                              style={{ backgroundColor: cat.color }}
                            />
                            {cat.name}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums">{rule.priority}</td>
                      <td className="px-4 py-2 text-center">
                        {rule.isActive ? (
                          <span className="text-success">●</span>
                        ) : (
                          <span className="text-secondary-300">○</span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-right">
                        <div className="flex justify-end gap-2">
                          <Button variant="secondary" onClick={() => openEditModal(rule)}>
                            <TbEdit />
                            {t('edit')}
                          </Button>
                          <Button variant="secondary" onClick={() => handleDelete(rule)}>
                            <TbTrash />
                            {t('delete')}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {!hideHeader && (
          <div className="flex justify-end">
            <Button variant="secondary" onClick={handleApplyAll} disabled={applying}>
              <TbFilter />
              {applying ? (t('applying') ?? 'Applying…') : (t('applyToExisting') ?? 'Apply to existing transactions')}
            </Button>
          </div>
        )}
      </div>

      <ModalLayout
        open={createOpen}
        setOpen={(o) => {
          setCreateOpen(o);
          if (!o) setEditing(null);
        }}
        closeButton
        className="z-50 mx-6 my-8 w-full rounded-[12px] bg-secondary-50 p-8 sm:max-w-[560px]">
        <div>
          <h2 className="mb-4 text-lg font-semibold">
            {editing ? (t('editTitle') ?? 'Edit rule') : (t('createTitle') ?? 'Create rule')}
          </h2>
          <div className="space-y-4">
            <Input
              id="rule-name"
              label={t('form.name') ?? 'Name'}
              type="text"
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
            />
            <Labeled label={t('form.matchType') ?? 'Match type'}>
              <DropdownSelect
                options={matchTypeOptions}
                setValue={(v) =>
                  setForm((p) => ({ ...p, matchType: v as typeof form.matchType }))
                }
                value={form.matchType}
              />
            </Labeled>
            <Input
              id="rule-match"
              label={t('form.matchText') ?? 'Pattern'}
              type="text"
              value={form.matchText}
              onChange={(e) => setForm((p) => ({ ...p, matchText: e.target.value }))}
              placeholder={t('form.matchTextPlaceholder') ?? 'e.g. RENT'}
            />
            <Labeled label={t('form.category') ?? 'Category'}>
              <DropdownSelect
                options={categoryOptions}
                setValue={(v) =>
                  setForm((p) => ({ ...p, financialCategoryId: typeof v === 'number' ? v : 0 }))
                }
                value={form.financialCategoryId || undefined}
              />
            </Labeled>
            <Input
              id="rule-priority"
              label={t('form.priority') ?? 'Priority (higher wins)'}
              type="number"
              inputMode="numeric"
              value={String(form.priority)}
              onChange={(e) =>
                setForm((p) => ({ ...p, priority: parseInt(e.target.value, 10) || 0 }))
              }
            />
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => setForm((p) => ({ ...p, isActive: e.target.checked }))}
              />
              <span>{t('form.active') ?? 'Active'}</span>
            </label>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="secondary" onClick={() => setCreateOpen(false)}>
                {t('cancel') ?? 'Cancel'}
              </Button>
              <Button variant="primary-dark" onClick={handleSubmit}>
                {editing ? (t('save') ?? 'Save') : (t('create') ?? 'Create')}
              </Button>
            </div>
          </div>
        </div>
      </ModalLayout>
    </>
  );
}